/**
 * Cart Drawer
 *
 * Server-rendered inside the overlay section group.
 * Uses event delegation so all listeners survive DOM refreshes.
 * Section Rendering API (`?sections=`) keeps the drawer in sync.
 */

/* ── helpers ─────────────────────────────────────────────────── */

function getCartSectionIds() {
  var els = document.querySelectorAll('[data-cart-section-id]');
  return Array.from(els).map(function(el) { return el.dataset.cartSectionId; });
}

function updateCartCount() {
  fetch('/cart.js')
    .then(function (r) { return r.json(); })
    .then(function (data) {  
      if(typeof window.updateCartCountDisplay === 'function') {
        window.updateCartCountDisplay(data.item_count);
      } else {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if(window.innerWidth > 991){
            el.innerHTML = '(' + data.item_count + ')';
          }else{
            el.innerHTML = data.item_count;
          }
        });
      }
    })
    .catch(console.error);
}

/* ── drawer open / close with focus‑trap (WCAG) ──────────────── */

var _previouslyFocused = null;

function openCartDrawer() {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  _previouslyFocused = document.activeElement;

  document.body.classList.add('open-drawer');
  document.body.classList.remove('open-menu', 'open-search', 'open-sort', 'open-filter', 'open-view-drawer');

  drawer.setAttribute('aria-hidden', 'false');
  drawer.inert = false;
  drawer.focus();
}

function closeCartDrawer() {
  var drawer = document.querySelector('.cart-sidebar');
  if (!drawer) return;

  document.body.classList.remove('open-drawer', 'open-menu', 'open-search', 'open-sort', 'open-filter','open-rv-filter', 'open-view-drawer');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.inert = true;

  if (_previouslyFocused && _previouslyFocused.focus) {
    _previouslyFocused.focus();
  }
}

/* Closed drawer starts out of the tab order */
document.addEventListener('DOMContentLoaded', function () {
  var drawer = document.querySelector('.cart-sidebar');
  if (drawer && !document.body.classList.contains('open-drawer')) {
    drawer.inert = true;
  }
});

/* ── auto-added products (the theme half of a native Shopify discount) ──
   snippets/cart-free-gift renders one verdict per "Free Gift / Auto-add" block
   on the Cart Drawer — does the cart meet that campaign's condition, is its
   product already in, did Shopify's discount actually price it — plus the keys
   of any orphaned lines left behind by a campaign that has since been deleted
   or switched off. This side only executes those verdicts. Shopify never adds
   the rewarded product itself, which is the whole reason this exists. */

/* One render to settle a change, one more to verify the discounts landed. */
var FREE_GIFT_MAX_PASSES = 2;

/* Cart fingerprint each campaign's discount check last failed on — the discount
   is paused, expired, or its conditions no longer match the block's. Without it
   the next sync would add the product straight back and loop. Keyed on the cart
   rather than latched for the page view, so changing the cart gets another try:
   the two thresholds may simply be one item apart. */
var _freeGiftFailures = {};

/* Same key, but for the console: an undiscounted gift that is being kept gets
   re-checked on every render, and one warning per cart state is plenty. */
var _freeGiftWarnings = {};

var _freeGiftSyncing = false;

var FREE_GIFT_DECLINED_KEY = 'freeGiftDeclined';

/* Campaigns the shopper turned down with the line's remove button. The gift is
   theirs to refuse, but the cart still qualifies, so without this the very next
   sync would put it back and the button would read as broken. Kept client-side
   (the cart is shared with checkout; a decline is not) and dropped again as
   soon as the cart stops qualifying, so re-qualifying re-offers the gift. */
function getDeclinedFreeGifts() {
  try {
    var raw = window.localStorage.getItem(FREE_GIFT_DECLINED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function setDeclinedFreeGifts(ids) {
  try {
    window.localStorage.setItem(FREE_GIFT_DECLINED_KEY, JSON.stringify(ids));
  } catch (e) { /* private mode / quota — the decline just won't outlive the page */ }
}

function declineFreeGift(ruleId) {
  if (!ruleId) return;
  var ids = getDeclinedFreeGifts();
  if (ids.indexOf(ruleId) === -1) {
    ids.push(ruleId);
    setDeclinedFreeGifts(ids);
  }
}

function getFreeGiftConfig() {
  var el = document.getElementById('cart-free-gift-config');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.error('Free gift: could not read config', e);
    return null;
  }
}

function changeFreeGiftLine(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    /* `id` with a line item key targets exactly one line — unlike `line`, it
       stays valid while the other lines in this same pass are changed. */
    body: JSON.stringify({ id: key, quantity: quantity })
  }).then(function (r) {
    if (!r.ok) throw new Error('Cart change failed');
    return r.json();
  });
}

function addFreeGiftLines(items) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: items })
  }).then(function (r) {
    if (!r.ok) throw new Error('Free gift add failed');
    return r.json();
  });
}

/**
 * Brings the cart in line with every campaign's verdict at once.
 * Resolves to true when the cart was changed (the caller then re-renders).
 */
function syncFreeGift() {
  var cfg = getFreeGiftConfig();
  if (!cfg || _freeGiftSyncing) return Promise.resolve(false);

  var removals = (cfg.orphans || []).slice();
  var quantityChanges = [];
  var additions = [];

  var declined = getDeclinedFreeGifts();
  var declinedChanged = false;

  (cfg.rules || []).forEach(function (rule) {
    /* An inactive campaign's line is already in cfg.orphans. */
    if (!rule.active) return;

    var declinedIndex = declined.indexOf(rule.id);

    /* Cart no longer qualifies: the decline has nothing left to suppress, so
       let it go — earning the gift again should offer it again. */
    if (declinedIndex !== -1 && !rule.qualifies) {
      declined.splice(declinedIndex, 1);
      declinedIndex = -1;
      declinedChanged = true;
    }

    var discountFailedHere = _freeGiftFailures[rule.id] === rule.signature;
    var wanted = rule.qualifies && rule.available && rule.variant_id > 0 &&
                 !discountFailedHere && declinedIndex === -1;

    if (!wanted) {
      /* Condition no longer met (or the product sold out) — take it back out. */
      if (rule.in_cart) removals.push(rule.line_key);
      return;
    }

    if (!rule.in_cart) {
      /* Underscore-prefixed: hidden from the shopper, still readable in Liquid
         and on the order. The campaign ID is what ties the line to its block. */
      var properties = { _free_gift: rule.id };
      if (rule.label) properties._free_gift_label = rule.label;
      additions.push({ id: rule.variant_id, quantity: rule.quantity, properties: properties });
      return;
    }

    if (rule.line_variant_id !== rule.variant_id) {
      /* Merchant swapped the product — drop the stale line, the next pass adds
         the new one. */
      removals.push(rule.line_key);
      return;
    }

    if (rule.line_quantity !== rule.quantity) {
      quantityChanges.push({ key: rule.line_key, quantity: rule.quantity });
      return;
    }

    if (!rule.discount_ok) {
      /* The block's condition is what decides whether the gift belongs in the
         cart; Shopify's discount only decides what it costs. So an undiscounted
         gift is reported and left alone — unless the merchant asked for the
         safety net, which trades the gift away rather than risk charging for
         it. That is what a gift flashing into the cart and vanishing a second
         later looks like: the add worked, Shopify just didn't discount it. */
      if (_freeGiftWarnings[rule.id] !== rule.signature) {
        _freeGiftWarnings[rule.id] = rule.signature;
        console.warn(
          'Free gift "' + (rule.label || rule.id) + '" is in the cart but Shopify did not discount it' +
          (rule.require_discount ? ', so it was removed again' : '') + '. Check that its discount is ' +
          'active and that — when more than one campaign is running — every discount involved has ' +
          '"Product discounts" ticked under Combinations.'
        );
      }

      if (rule.require_discount) {
        _freeGiftFailures[rule.id] = rule.signature;
        removals.push(rule.line_key);
      }
    }
  });

  if (declinedChanged) setDeclinedFreeGifts(declined);

  if (!removals.length && !quantityChanges.length && !additions.length) {
    return Promise.resolve(false);
  }

  /* Line changes go one at a time (the cart API takes a single line per call),
     every addition rides in one request. Removals lead, so a swapped product
     never briefly sits in the cart twice. */
  _freeGiftSyncing = true;
  var chain = Promise.resolve();
  removals.forEach(function (key) {
    chain = chain.then(function () { return changeFreeGiftLine(key, 0); });
  });
  quantityChanges.forEach(function (change) {
    chain = chain.then(function () { return changeFreeGiftLine(change.key, change.quantity); });
  });
  if (additions.length) {
    chain = chain.then(function () { return addFreeGiftLines(additions); });
  }

  return chain
    .then(function () { return true; })
    .catch(function (error) {
      console.error('Free gift sync failed:', error);
      return false;
    })
    .finally(function () { _freeGiftSyncing = false; });
}

/* ── core: refresh cart drawer via Section Rendering API ───── */

/**
 * `depth` guards the free-gift sync loop: a sync that changed the cart needs
 * another render to settle (and, for the discount check, one more to verify the
 * freshly added gift really got discounted), but never more than that.
 */
function refreshCart(depth) {
  var pass = typeof depth === 'number' ? depth : 0;

  var sectionIds = getCartSectionIds();
  if (!sectionIds.length) return Promise.resolve();

  var query = sectionIds.join(',');

  return fetch(window.Shopify.routes.root + '?sections=' + query)
    .then(function (response) { return response.json(); })
    .then(function (data) {
      sectionIds.forEach(function(sectionId) {
        if (!data[sectionId]) return;

        var wrapper = document.querySelector('[data-cart-section-id="' + sectionId + '"]');
        if (!wrapper) {
            wrapper = document.getElementById('shopify-section-' + sectionId);
        }
        
        if (!wrapper) return;

        var parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
        var fresh  = parsed.querySelector('[data-cart-section-id="' + sectionId + '"]') || parsed.getElementById('shopify-section-' + sectionId);
        
        if (fresh) {
          var cartBody = wrapper.querySelector('.cart-drawer-body');
          var scrollTop = cartBody ? cartBody.scrollTop : 0;
          if (cartBody) cartBody.style.minHeight = cartBody.scrollHeight + 'px';

          var currentEmpty = wrapper.querySelector('.cart-drawer-empty, .cart-empty-page');
          var freshEmpty = fresh.querySelector('.cart-drawer-empty, .cart-empty-page');
          
          if ((currentEmpty && !freshEmpty) || (!currentEmpty && freshEmpty)) {
            // State changed from empty to non-empty or vice-versa
            wrapper.innerHTML = fresh.innerHTML;
          } else if (!currentEmpty && !freshEmpty) {
            // Selectively update dynamic parts to avoid destroying sliders/tickers
            var selectors = [
              '.cart-count-wrapper',
              '.drawer-header',
              '.subtotal-row',
              '.cart-drawer-subtotal',
              '.cart-drawer-gift-packaging',
              '.gift-packaging-row',
              '.cart-footer-discount-wrapper',
              '.cart-footer-total',
              '.cart-free-gift-messages'
            ];
            selectors.forEach(function(selector) {
              var currentEls = wrapper.querySelectorAll(selector);
              var freshEls = fresh.querySelectorAll(selector);
              currentEls.forEach(function(currentEl, index) {
                var freshEl = freshEls[index];
                if (currentEl && freshEl) {
                  currentEl.innerHTML = freshEl.innerHTML;
                }
              });
            });

            var currentMiddle = wrapper.querySelector('.drawer-middle, .cart-items-list');
            var freshMiddle = fresh.querySelector('.drawer-middle, .cart-items-list');
            if (currentMiddle && freshMiddle) {
               var cRows = Array.prototype.slice.call(currentMiddle.querySelectorAll('.item-row'));
               var fRows = Array.prototype.slice.call(freshMiddle.querySelectorAll('.item-row'));
               
               // Step 1: Remove rows that no longer exist in the new cart state (e.g. removed item)
               cRows.forEach(function(cRow) {
                 var existsInFresh = fRows.find(function(f) { return f.dataset.variantId === cRow.dataset.variantId; });
                 if (!existsInFresh) {
                   cRow.parentNode.removeChild(cRow);
                 }
               });

               // Step 2: Iterate over fresh rows and sync them into the current DOM
               fRows.forEach(function(fRow, index) {
                  var cRow = currentMiddle.children[index];
                  
                  if (cRow && cRow.dataset.variantId === fRow.dataset.variantId) {
                     // Granular update for matching row
                     var cQty = cRow.querySelector('.qty-input');
                     var fQty = fRow.querySelector('.qty-input');
                     if (cQty && fQty) cQty.value = fQty.value;
                     
                     var cPrice = cRow.querySelector('.item-price');
                     var fPrice = fRow.querySelector('.item-price');
                     if (cPrice && fPrice) cPrice.innerHTML = fPrice.innerHTML;

                     var cSelected = cRow.querySelector('.selected-value');
                     var fSelected = fRow.querySelector('.selected-value');
                     if (cSelected && fSelected) cSelected.innerHTML = fSelected.innerHTML;

                     var cOptions = cRow.querySelector('.selector-options');
                     var fOptions = fRow.querySelector('.selector-options');
                     if (cOptions && fOptions) cOptions.innerHTML = fOptions.innerHTML;

                     // Update the index lines so JS continues to target the correct item
                     cRow.dataset.line = fRow.dataset.line;
                     var cRemove = cRow.querySelector('.js-item-remove');
                     var fRemove = fRow.querySelector('.js-item-remove');
                     if (cRemove && fRemove) cRemove.dataset.line = fRemove.dataset.line;

                     var cVariantDrop = cRow.querySelector('.js-custom-variant-selector');
                     var fVariantDrop = fRow.querySelector('.js-custom-variant-selector');
                     if (cVariantDrop && fVariantDrop) cVariantDrop.dataset.line = fVariantDrop.dataset.line;

                  } else {
                     // New row or shifted different variant. Insert the new HTML.
                     if (cRow) {
                        cRow.insertAdjacentHTML('beforebegin', fRow.outerHTML);
                     } else {
                        currentMiddle.appendChild(fRow.cloneNode(true));
                     }
                  }
               });

               // Step 3: Cleanup any extra dangling rows at the bottom
               while(currentMiddle.children.length > fRows.length) {
                  currentMiddle.removeChild(currentMiddle.lastElementChild);
               }
            }
          } else {
            // Both are empty
            wrapper.innerHTML = fresh.innerHTML;
          }

          /* The verdict JSON sits outside .cart-drawer-body, so the granular
             branch above never touches it — without this the next sync would
             act on the pre-mutation cart. */
          var currentGiftConfig = wrapper.querySelector('#cart-free-gift-config');
          var freshGiftConfig = fresh.querySelector('#cart-free-gift-config');
          if (currentGiftConfig && freshGiftConfig) {
            currentGiftConfig.textContent = freshGiftConfig.textContent;
          }

          var updatedCartBody = wrapper.querySelector('.cart-drawer-body');
          if (updatedCartBody) {
            updatedCartBody.style.minHeight = '';
            updatedCartBody.scrollTop = scrollTop;
          }
        }
      });

      /* keep drawer visibly open if it was open */
      var newDrawer = document.querySelector('.cart-sidebar');
      if (newDrawer) {
        var drawerIsOpen = document.body.classList.contains('open-drawer');
        newDrawer.setAttribute('aria-hidden', drawerIsOpen ? 'false' : 'true');
        newDrawer.inert = !drawerIsOpen;
      }

      updateCartCount();
      initSplide();
      initUspTicker();
      initCartAccordion();
      if (typeof window.initCartFooterFixed === 'function') {
        window.initCartFooterFixed();
      }

      var cartDrawer = document.querySelector('.cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('is-updating');

      /* The just-rendered sections carry a fresh free-gift verdict; act on it. */
      if (pass >= FREE_GIFT_MAX_PASSES) return;
      return syncFreeGift().then(function (changed) {
        if (changed) return refreshCart(pass + 1);
      });
    })
    .catch(function (error) {
      console.error('Error refreshing cart:', error);
    });
}

/** Backward-compatible alias – theme.js calls load_cart() */
function load_cart() {
  return refreshCart();
}

/* ── Splide init (scoped to cart drawer) ─────────────────────── */

function initSplide() {
  var sectionId = getCartSectionIds()[0];
  if (!sectionId || typeof Splide === 'undefined') return;

  var el = document.getElementById('splide-' + sectionId);
  if (!el) return;

  /* avoid double-init */
  if (el.classList.contains('is-initialized')) return;

  new Splide('#splide-' + sectionId, {
    type: 'slide',
    perPage: 2,
    gap: '0.75rem',
    pagination: false,
    arrows: true,
    breakpoints: {
      600: { perPage: 1 }
    },
    i18n: {
      prev: 'Previous upsell product',
      next: 'Next upsell product',
      slide: 'Go to upsell product %s'
    }
  }).mount();
}

/* ── cart mutations ───────────────────────────────────────────── */

function updateCartItemQuantity(line, quantity) {
  var cartDrawer = document.querySelector('.cart-drawer');
  if (cartDrawer) cartDrawer.classList.add('is-updating');

  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line: line, quantity: quantity })
  })
    .then(function (r) { return r.json(); })
    .then(function () { refreshCart(); })
    .catch(function (e) { console.error('Error updating cart:', e); });
}

/* ── discount helpers ────────────────────────────────────────── */

function fetchCurrentDiscountCodes() {
  return fetch('/cart.js', { method: 'GET' })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      var codes = [];
      if (data.cart_level_discount_applications) {
        data.cart_level_discount_applications.forEach(function (d) {
          if (d.type === 'discount_code') codes.push(d.title);
        });
      }
      if (data.items) {
        data.items.forEach(function (item) {
          if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
            item.line_level_discount_allocations.forEach(function (d) {
              if (d.discount_application && d.discount_application.type === 'discount_code') {
                codes.push(d.discount_application.title);
              }
            });
          }
        });
      }
      return codes.filter(function (v, i, a) { return a.indexOf(v) === i; });
    });
}

function applyDiscountCode(discountCode, btn) {
  var wrapper = btn ? btn.closest('.custom-discount-form') : document.querySelector('.custom-discount-form');
  var errorEl = wrapper ? wrapper.querySelector('p.error') : document.querySelector('.custom-discount-form p.error');
  
  if (btn) {
    btn.classList.add('loading');
    btn.disabled = true;
  }

  fetchCurrentDiscountCodes().then(function (codes) {
    // Avoid duplicates
    if (codes.indexOf(discountCode) === -1) {
      codes.push(discountCode);
    }

    fetch('/checkout?discount=' + codes.join(','), { method: 'GET', redirect: 'manual' })
      .finally(function () {
        fetch('/cart.js', { method: 'GET' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var applied = [];
            if (data.cart_level_discount_applications) {
              data.cart_level_discount_applications.forEach(function (d) {
                if (d.type === 'discount_code') applied.push(d.title);
              });
            }
            data.items.forEach(function (item) {
              if (item.line_level_discount_allocations) {
                item.line_level_discount_allocations.forEach(function (d) {
                  if (d.discount_application && d.discount_application.type === 'discount_code') {
                    applied.push(d.discount_application.title);
                  }
                });
              }
            });
            
            // Case-insensitive check for application success
            var isApplied = applied.some(function(c) { 
              return c.toLowerCase() === discountCode.toLowerCase(); 
            });

            if (!isApplied) {
              if (errorEl) {
                errorEl.style.display = 'block';
                errorEl.textContent = 'Enter a valid discount code or gift card';
              }
              if (btn) {
                btn.classList.remove('loading');
                btn.disabled = false;
              }
            } else {
              refreshCart().then(function() {
                // After refresh, if we're on the cart page or drawer, 
                // the form might be hidden again. The refresh replaces HTML.
                // We could potentially reopen it here if needed, but 
                // refreshCart replaces the entire section.
              });
            }
          })
          .catch(function() {
            if (btn) {
              btn.classList.remove('loading');
              btn.disabled = false;
            }
          });
      });
  });
}

function removeDiscountCode(code) {
  fetchCurrentDiscountCodes().then(function (codes) {
    var remaining = codes.filter(function (c) { return c !== code; });
    fetch('/checkout?discount=' + remaining.join(','), { method: 'GET', redirect: 'manual' })
      .finally(function () { refreshCart(); });
  });
}

/* ── delegated click handler (survives DOM refreshes) ────────── */

document.addEventListener('click', function (e) {

  /* Open cart drawer */
  if (e.target.closest('.header-cart')) {
    e.preventDefault();
    openCartDrawer();
    return;
  }

  /* Close cart drawer */
  if (e.target.closest('.overlay-box') || e.target.closest('.cart-drawer-close')) {
    closeCartDrawer();
    return;
  }

  /* Remove line item */
  var removeBtn = e.target.closest('.js-item-remove');
  if (removeBtn) {
    e.preventDefault();
    /* Removing an auto-added gift has to be remembered, or the campaign — whose
       condition the cart still meets — would simply add it back. */
    var giftRow = removeBtn.closest('[data-free-gift]');
    if (giftRow) declineFreeGift(giftRow.dataset.freeGift);
    updateCartItemQuantity(removeBtn.dataset.line, 0);
    return;
  }

  /* Qty + (scoped to .cart-drawer) */
  if (e.target.closest('.qty-plus')) {
    var row = e.target.closest('.item-row');
    if (!row) return;
    var input = row.querySelector('.qty-input');
    var line = row.dataset.line;
    if (!input || !line) return;
    updateCartItemQuantity(line, (parseInt(input.value, 10) || 1) + 1);
    return;
  }

  /* Qty − */
  if (e.target.closest('.qty-minus')) {
    var row2   = e.target.closest('.item-row');
    if (!row2) return;
    var input2 = row2.querySelector('.qty-input');
    var line2 = row2.dataset.line;
    if (!input2 || !line2) return;
    var qty = parseInt(input2.value, 10) || 1;
    updateCartItemQuantity(line2, qty > 1 ? qty - 1 : 0);
    return;
  }

  /* Gift packaging checkbox */
  var giftCheckbox = e.target.closest('.js-gift-packaging-checkbox');
  if (giftCheckbox) {
    var varId = giftCheckbox.dataset.variantId;
    if (!varId) return;

    if (giftCheckbox.checked) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: varId, quantity: 1 })
      })
      .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function () { refreshCart(); })
      .catch(function (err) { 
        console.error('Gift packaging add error:', err);
        giftCheckbox.checked = false;
      });
    } else {
      /* Remove gift packaging - find line item first */
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var line = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].variant_id == varId) {
              line = i + 1;
              break;
            }
          }
          if (line) {
            updateCartItemQuantity(line, 0);
          }
        });
    }
    return;
  }

  /* Upsell add-to-cart */
  var upsellBtn = e.target.closest('.js-upsell-add-to-cart');
  if (upsellBtn) {
    var variantId = upsellBtn.dataset.variantId;
    if (!variantId) return;
    upsellBtn.classList.add('loading');
    upsellBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Add to cart failed');
        return r.json();
      })
      .then(function () { refreshCart(); })
      .catch(function (err) { console.error('Upsell add to cart error:', err); })
      .finally(function () {
        upsellBtn.classList.remove('loading');
        upsellBtn.disabled = false;
      });
    return;
  }

  /* Apply discount code */
  var submitDiscount = e.target.closest('.submit-discount');
  if (submitDiscount) {
    var wrapper  = submitDiscount.closest('.custom-discount-form');
    var inputEl  = wrapper.querySelector('input');
    var errorEl2 = wrapper.querySelector('p.error');
    var code2    = inputEl ? inputEl.value.trim() : '';
    if (errorEl2) errorEl2.style.display = 'none';

    if (code2 && code2.length) {
      applyDiscountCode(code2, submitDiscount);
    } else if (errorEl2) {
      errorEl2.style.display = 'block';
      errorEl2.textContent = 'Enter a valid discount code or gift card';
    }
    return;
  }

  /* Remove discount code */
  var removeDiscount = e.target.closest('.remove-discount');
  if (removeDiscount) {
    removeDiscountCode(removeDiscount.dataset.code);
    return;
  }

  /* Custom Variant Selector Toggle */
  var selectorToggle = e.target.closest('.js-custom-variant-selector .selector-toggle');
  if (selectorToggle) {
    var parent = selectorToggle.closest('.js-custom-variant-selector');
    var isOpen = parent.classList.contains('is-open');
    
    // Close other open selectors first
    document.querySelectorAll('.js-custom-variant-selector.is-open').forEach(function(el) {
      if (el !== parent) el.classList.remove('is-open');
    });

    parent.classList.toggle('is-open');
    selectorToggle.setAttribute('aria-expanded', !isOpen);
    return;
  }

  /* Custom Variant Selector Option Click */
  var option = e.target.closest('.selector-option');
  if (option) {
    var parent = option.closest('.js-custom-variant-selector');
    var newVariantId = option.dataset.value;

    if (option.classList.contains('is-active')) {
      parent.classList.remove('is-open');
      return;
    }

    var row = option.closest('.item-row');
    var activeOption = parent.querySelector('.selector-option.is-active');
    var oldVariantId = (row && row.dataset.variantId) || (activeOption && activeOption.dataset.value);
    var line = row ? row.dataset.line : null;

    // Show a loading state on the row and the drawer while it is removed + re-added (cleared on refresh).
    if (row) row.classList.add('is-updating');
    var cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-updating');

    updateCartItemVariant(oldVariantId, newVariantId, line);
    parent.classList.remove('is-open');
    return;
  }

  /* Click Outside to Close Selectors */
  if (!e.target.closest('.js-custom-variant-selector')) {
    document.querySelectorAll('.js-custom-variant-selector.is-open').forEach(function(el) {
      el.classList.remove('is-open');
    });
  }
});

/* ── delegated change handler (survives DOM refreshes) ────────── */

document.addEventListener('change', function (e) {
  // We handle variant switching via custom click handler now
});

/* Serialize variant swaps: run them strictly one at a time so concurrent changes on
   different products can't interleave cart mutations (which duplicated or dropped items). */
var variantUpdateQueue = Promise.resolve();

function updateCartItemVariant(oldVariantId, newVariantId, line) {
  if (!oldVariantId || !newVariantId) return variantUpdateQueue;

  variantUpdateQueue = variantUpdateQueue.then(function() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Try matching by the exact line index first, fallback to filtering by variant ID.
        var item = null;
        if (line) {
          item = cart.items[parseInt(line, 10) - 1];
        }
        if (!item || String(item.variant_id) !== String(oldVariantId)) {
          item = cart.items.filter(function(it) {
            return String(it.variant_id) === String(oldVariantId);
          })[0];
        }
        
        if (!item) return;
        var qty = item.quantity;
        var oldKey = item.key;

        // Remove the old line first (by stable key), then add the new variant.
        // Chaining res.json() ensures the HTTP transaction is fully finished, releasing the Shopify session lock.
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: oldKey, quantity: 0 })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to remove variant');
          return res.json();
        })
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: newVariantId, quantity: qty })
          });
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Failed to add new variant');
          return res.json();
        });
      })
      .then(function() { return refreshCart(); })
      .catch(function(err) {
        console.error('Error switching variant:', err);
        return refreshCart();
      });
  });

  return variantUpdateQueue;
}

/* ── keyboard: Escape to close + focus trap ──────────────────── */

document.addEventListener('keydown', function (e) {
  /* Discount code "Enter" key */
  if (e.key === 'Enter') {
    var discountInput = e.target.closest('#DiscountCode');
    if (discountInput) {
      e.preventDefault();
      var wrapper = discountInput.closest('.custom-discount-form');
      var btn = wrapper.querySelector('.submit-discount');
      if (btn) btn.click();
      return;
    }
  }

  if (e.key === 'Escape' && document.body.classList.contains('open-drawer')) {
    closeCartDrawer();
    return;
  }

  /* Focus trap inside the drawer */
  if (e.key === 'Tab' && document.body.classList.contains('open-drawer')) {
    var drawer = document.querySelector('.cart-sidebar');
    if (!drawer) return;

    var focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
});

/* ── Product Recommendations Element ─────────────────────────── */

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', class extends HTMLElement {
    constructor() {
      super();
      var self = this;
      var observer = new IntersectionObserver(function(entries, observer) {
        if (entries[0].isIntersecting) {
          observer.unobserve(self);
          self.loadRecommendations();
        }
      }, { threshold: 0.1 });
      observer.observe(this);
    }

    loadRecommendations() {
      var self = this;
      fetch(this.dataset.url)
        .then(function(response) { return response.text(); })
        .then(function(html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var recommendations = doc.querySelector('product-recommendations');
          if (recommendations && recommendations.innerHTML.trim().length > 0) {
            self.innerHTML = recommendations.innerHTML;
          }
        })
        .catch(function(e) { console.error('Error loading recommendations:', e); });
    }
  });
}

/* ── USP Ticker ─────────────────────────────────────────────── */
var isUspPaused = false;

function initUspTicker() {
  document.querySelectorAll('[data-usp-ticker]').forEach(function(ticker) {
    // Clear existing interval if any
    if (ticker._uspInterval) {
      clearInterval(ticker._uspInterval);
      ticker._uspInterval = null;
    }

    var items = ticker.querySelectorAll('[data-usp-item]');
    if (items.length <= 1) return;

    var toggleBtn = ticker.closest('.cart-drawer-usps-wrapper').querySelector('.video-toggle');
    
    if (isUspPaused) {
      if (toggleBtn) toggleBtn.classList.add('is-paused');
      return;
    }

    if (toggleBtn) toggleBtn.classList.remove('is-paused');

    var currentIndex = 0;
    // Find already active item if any
    items.forEach(function(item, idx) {
      if (item.classList.contains('active')) currentIndex = idx;
    });

    ticker._uspInterval = setInterval(function() {
      if (isUspPaused) return;

      var currentItem = items[currentIndex];
      currentItem.classList.add('fade-out');

      setTimeout(function() {
        currentItem.classList.remove('active', 'fade-out');
        currentIndex = (currentIndex + 1) % items.length;
        var nextItem = items[currentIndex];
        nextItem.classList.add('active');
      }, 500);
    }, 3500);
  });
}
function initCartAccordion() {
  var accordions = document.querySelectorAll('.cart-drawer-accordion');
  if (!accordions.length) return;

  accordions.forEach(function(el) {
    var title = el.querySelector('.cart-drawer-accordion__title');
    var body = el.querySelector('.cart-drawer-accordion__body');
    if (!title || !body) return;

    // Prevent duplicate listeners
    if (title.hasAccordionListener) return;
    title.hasAccordionListener = true;

    // Set initial state
    var isOpen = el.classList.contains('is-open');
    if (isOpen) {
      body.style.maxHeight = 'none';
    } else {
      body.style.maxHeight = '0px';
    }

    title.addEventListener('click', function() {
      var currentlyOpen = el.classList.contains('is-open');
      title.setAttribute('aria-expanded', currentlyOpen ? 'false' : 'true');

      if (currentlyOpen) {
        // Closing
        body.style.maxHeight = body.scrollHeight + 'px';
        body.offsetHeight; // Force reflow
        body.style.maxHeight = '0px';
        el.classList.remove('is-open');
      } else {
        // Opening
        el.classList.add('is-open');
        body.style.maxHeight = body.scrollHeight + 'px';

        var onTransitionEnd = function(event) {
          if (event.propertyName === 'max-height') {
            if (el.classList.contains('is-open')) {
              body.style.maxHeight = 'none';
            }
            body.removeEventListener('transitionend', onTransitionEnd);
          }
        };
        body.addEventListener('transitionend', onTransitionEnd);
      }
    });

    title.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        title.click();
      }
    });
  });
}
/* ── initialise ──────────────────────────────────────────────── */

updateCartCount();
initSplide();
initUspTicker();
initCartAccordion();

/* Ticker play/pause click handler */
document.addEventListener('click', function(e) {
  var toggle = e.target.closest('.video-toggle');
  if (toggle) {
    isUspPaused = !isUspPaused;
    
    // Update all toggles to maintain visual consistency
    document.querySelectorAll('.video-toggle').forEach(function(btn) {
      btn.classList.toggle('is-paused', isUspPaused);
    });
    
    if (isUspPaused) {
      document.querySelectorAll('[data-usp-ticker]').forEach(function(ticker) {
        if (ticker._uspInterval) {
          clearInterval(ticker._uspInterval);
          ticker._uspInterval = null;
        }
      });
    } else {
      initUspTicker();
    }
  }
});

/* The cart can arrive at this page already qualifying (added in another tab, a
   recovered cart, a cached page), so reconcile the gifts once on load. */
syncFreeGift().then(function (changed) {
  if (changed) refreshCart(1);
});
